/**
 * Event page — organizer side (Oct 11, 2026 — Dennis; claude/Event_Page_Plan_Oct11.md).
 *
 * EventPagePanel: the "Event page" block inside Event Setup (App.js EventSetupModal,
 * existing events only, not on the Event Desk). Shows Live / Not published and opens
 * the editor.
 *
 * EventPageEditor: full-screen. Form on the left, the real page (EventPageView) on the
 * right as a live preview, Desktop / Phone and Before / On the day / After. Save keeps
 * the live state; Publish puts it live at the event's address; Unpublish takes it down.
 *
 * API: GET/PUT /api/events/:id/page (eventRoutes.js → services/eventPageService.js).
 *
 * Oct 23, 2026 (Dennis): a Donate button (his own link + wording) and Sponsors (name, and
 * optionally a logo and a website). Sponsor logos upload straight away to
 * POST /api/events/:id/page/sponsors/:sid/logo, so the preview shows them; a sponsor taken
 * off the list loses its logo when the page is saved.
 *
 * Oct 23, 2026 (Dennis): "Entry fees & how to pay" — optional prices (per golfer / per
 * team) and the ways the organizer already collects. Tick a way, enter the account name;
 * TTO builds the links (EventPage.js payOptions). Nothing ticked → nothing about paying
 * anywhere. Saved inside the page as page.payment (services/eventPaymentService.js).
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { EventPageView, phaseFor, useEventPageFonts, payAmount } from "./EventPage";

const GREEN = "#2d6a4f";
const FIELD = { width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 8, padding: "9px 10px", fontSize: 14, fontFamily: "inherit", color: "#111827", background: "#fff" };
const LABEL = { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 };
const HINT = { fontSize: 12, color: "#6b7280", marginTop: 4, lineHeight: 1.45 };
const H2 = { margin: "0 0 12px", fontSize: 15, fontWeight: 800, color: "#111827" };
const SECTION = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "16px 16px 18px" };

function authHeaders(getToken, json) {
  const h = {};
  if (json) h["Content-Type"] = "application/json";
  const t = getToken && getToken();
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

async function callPage(apiUrl, getToken, eventId, method, body) {
  const res = await fetch(`${apiUrl}/events/${eventId}/page`, {
    method,
    credentials: "include",
    headers: authHeaders(getToken, !!body),
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch (_) { /* empty */ }
  if (!res.ok) {
    const e = new Error((data && data.error) || "Something went wrong. Please try again.");
    e.status = res.status;
    throw e;
  }
  return data;
}

function copyText(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(text);
  return Promise.reject(new Error("no clipboard"));
}

// --------------------------------------------------------------------------
// Panel inside Event Setup
// --------------------------------------------------------------------------
export default function EventPagePanel({ eventId, apiUrl, getToken, addToast }) {
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    callPage(apiUrl, getToken, eventId, "GET")
      .then((d) => { if (!cancelled) setInfo(d); })
      .catch((e) => { if (!cancelled) setLoadError(e.message); });
    return () => { cancelled = true; };
  }, [eventId, apiUrl, getToken]);

  if (!eventId) return null;
  const live = info && info.published;
  return (
    <div>
      <div style={LABEL}>Event page</div>
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: live ? "#f0fdf4" : "#f9fafb" }}>
        {loadError && <div style={{ fontSize: 13, color: "#b91c1c" }}>{loadError}</div>}
        {!info && !loadError && <div style={{ fontSize: 13, color: "#6b7280" }}>Loading…</div>}
        {info && (
          <>
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.45 }}>
              {live ? (
                <>
                  <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#16a34a", marginRight: 6 }} />
                  <strong>Live</strong> at <a href={info.page_url} target="_blank" rel="noreferrer" style={{ color: GREEN, fontWeight: 700 }}>{info.page_address}</a>
                </>
              ) : (
                <>A public page for this event — background photo, description, a Register button and the live leaderboard link — at <strong>{info.page_address}</strong>. {info.saved ? "Saved, not published yet." : "Not set up yet."}</>
              )}
            </div>
            <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
              <button type="button" onClick={() => setOpen(true)}
                style={{ background: GREEN, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                {info.saved ? "Edit event page" : "Set up event page"}
              </button>
              {live && (
                <button type="button"
                  onClick={() => copyText(info.page_url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => addToast && addToast("Could not copy — select the address and copy it instead", "error"))}
                  style={{ background: "none", border: "none", color: GREEN, fontWeight: 600, fontSize: 13, cursor: "pointer", padding: 0 }}>
                  {copied ? "✓ Copied" : "Copy link"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
      {open && info && (
        <EventPageEditor
          eventId={eventId}
          initial={info}
          apiUrl={apiUrl}
          getToken={getToken}
          addToast={addToast}
          onChange={setInfo}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Editor
// --------------------------------------------------------------------------
function formFrom(info) {
  const p = info.page || {};
  return {
    presented_by: p.presented_by || "",
    title: p.title || "",
    date_text: p.date_text || "",
    course_text: p.course_text || "",
    checkin_text: p.checkin_text || "",
    start_text: p.start_text || "",
    format_text: p.format_text || "",
    // First visit: start from the event's own short description, if it has one.
    description: p.description || (!info.saved ? (info.event_description || "") : ""),
    registration_mode: p.registration_mode || "none",
    registration_url: p.registration_url || "",
    registration_label: p.registration_label || "",
    contact_name: p.contact_name || "",
    contact_email: p.contact_email || "",
    contact_phone: p.contact_phone || "",
    show_leaderboard: p.show_leaderboard !== false,
    background: p.background || { kind: "library", key: "sunset-green" },
    donate_url: p.donate_url || "",
    donate_label: p.donate_label || "",
    sponsors: (p.sponsors || []).map((x) => ({ id: x.id, name: x.name || "", url: x.url || "" })),
    pay_fees: ((p.payment && p.payment.fees) || []).map((f) => ({ label: f.label || "", per: f.per === "team" ? "team" : "golfer", amount: f.amount || "" })),
    pay_methods: payMethodsForm((p.payment && p.payment.methods) || []),
  };
}

// Payment links (Oct 23, 2026): the ways to pay, in the order the page lists them.
export const PAY_METHODS = [
  { type: "venmo", name: "Venmo", field: "Venmo username", placeholder: "e.g. SeacoastGolf (the name after the @)" },
  { type: "paypal", name: "PayPal", field: "PayPal.Me name", placeholder: "e.g. SeacoastGolf (from paypal.me/SeacoastGolf)" },
  { type: "cashapp", name: "Cash App", field: "$cashtag", placeholder: "e.g. $SeacoastGolf" },
  { type: "zelle", name: "Zelle", field: "Email or mobile number your Zelle uses", placeholder: "e.g. golf@seacoast.org" },
  { type: "check", name: "Check", field: "Make checks payable to", placeholder: "e.g. Seacoast Church" },
  { type: "checkin", name: "Pay at check-in", field: null },
  { type: "link", name: "My own pay-online page", field: "Web address", placeholder: "e.g. seacoast.org/pay" },
];

/** Stored list → the editor's { venmo: { on, handle, label, url, note }, … }. */
export function payMethodsForm(list) {
  const out = {};
  for (const m of PAY_METHODS) {
    const got = (list || []).find((x) => x && x.type === m.type);
    out[m.type] = { on: !!got, handle: (got && got.handle) || "", label: (got && got.label) || "", url: (got && got.url) || "", note: (got && got.note) || "" };
  }
  return out;
}

/** The editor's form → the raw payment shape the public page gets (null = nothing to show). */
export function previewPayment(form, title, payee) {
  const methods = [];
  for (const m of PAY_METHODS) {
    const f = (form.pay_methods || {})[m.type];
    if (!f || !f.on) continue;
    if (m.type === "checkin") methods.push({ type: m.type, note: f.note.trim() });
    else if (m.type === "link") { if (f.url.trim()) methods.push({ type: m.type, url: withScheme(f.url.trim()), label: f.label.trim() || "Pay online" }); }
    else if (f.handle.trim()) {
      const h = f.handle.trim().replace(/^https?:\/\//i, "").replace(/^(www\.)?(venmo\.com\/(u\/)?|paypal\.me\/|cash\.app\/)/i, "").replace(/^[@$]/, "");
      methods.push({ type: m.type, handle: h, note: m.type === "check" ? f.note.trim() : undefined });
    }
  }
  if (!methods.length) return null;
  const fees = (form.pay_fees || []).map((f) => ({ label: f.label.trim() || (f.per === "team" ? "Per team" : "Per golfer"), per: f.per, amount: payAmount(f.amount) })).filter((f) => f.amount);
  return { payee: payee || "", event_name: title || "", fees, methods };
}

/** A sponsor row's id, made in the browser so its logo can upload before the page is saved. */
export function newSponsorId() {
  return (Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4)).replace(/[^a-z0-9]/g, "0").padEnd(8, "0");
}

function withScheme(u) { return /^https?:/i.test(u) ? u : `https://${u}`; }

/** The editor's form → the same shape the public API returns, for the preview. */
export function previewData(form, info) {
  const lib = (info.library || []).find((x) => form.background.kind === "library" && x.key === form.background.key);
  const col = (info.colors || []).find((x) => form.background.kind === "color" && x.key === form.background.key);
  const up = form.background.kind === "upload" && info.upload ? info.upload : null;
  const background = up
    ? { kind: "photo", key: "upload", position: `${form.background.x}% ${form.background.y}%`, scrim: 0.45, url: up.url, url_small: up.url_small, bg: "#173F2C", ink: "#FFFFFF", accent: "#E3BC5E", accentInk: "#1D2420" }
    : lib
    ? { kind: "photo", key: lib.key, position: lib.position, scrim: lib.scrim, url: `/event-backgrounds/${lib.key}-2400.webp`, url_small: `/event-backgrounds/${lib.key}-1200.webp`, bg: "#173F2C", ink: "#FFFFFF", accent: "#E3BC5E", accentInk: "#1D2420" }
    : col ? { kind: "color", ...col } : undefined;
  const d = info.derived || {};
  let registration = null;
  if (form.registration_mode === "link" && form.registration_url.trim()) registration = { url: /^https?:/i.test(form.registration_url.trim()) ? form.registration_url.trim() : `https://${form.registration_url.trim()}`, label: form.registration_label.trim() || "Register" };
  if (form.registration_mode === "invite") registration = { url: info.invite_url || "#", label: form.registration_label.trim() || "Sign up" };
  const contact = (form.contact_name.trim() || form.contact_email.trim() || form.contact_phone.trim())
    ? { name: form.contact_name.trim(), email: form.contact_email.trim(), phone: form.contact_phone.trim() } : null;
  return {
    published: true,
    event_date: info.event_date,
    presented_by: form.presented_by.trim(),
    title: form.title.trim() || d.title,
    date_text: form.date_text.trim() || d.date_text,
    course_text: form.course_text.trim() || d.course_text,
    checkin_text: form.checkin_text.trim(),
    start_text: form.start_text.trim() || d.start_text,
    format_text: form.format_text.trim(),
    description: form.description.trim(),
    registration,
    contact,
    leaderboard: form.show_leaderboard && info.leaderboard_address ? { url: `https://${info.leaderboard_address}`, address: info.leaderboard_address } : null,
    background,
    donate: form.donate_url.trim() ? { url: withScheme(form.donate_url.trim()), label: form.donate_label.trim() || "Donate" } : null,
    sponsors: form.sponsors.filter((x) => x.name.trim()).map((x) => ({
      name: x.name.trim(),
      url: x.url.trim() ? withScheme(x.url.trim()) : null,
      logo_url: (info.sponsor_logos || {})[x.id] || null,
    })),
    logo_url: info.logo_url || null,
    links: null,
    payment: previewPayment(form, form.title.trim() || d.title, form.presented_by.trim() || info.group_name || ""),
  };
}

function Field({ label, hint, children }) {
  return (
    <label style={{ display: "block" }}>
      <span style={LABEL}>{label}</span>
      {children}
      {hint && <div style={HINT}>{hint}</div>}
    </label>
  );
}

/** Renders the page at a fixed width, scaled to fit its box. */
function ScaledPreview({ data, device, phase }) {
  const boxRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState(0);
  const pageWidth = device === "phone" ? 390 : 1200;
  useLayoutEffect(() => {
    const measure = () => {
      const box = boxRef.current;
      if (!box) return;
      const w = box.clientWidth || pageWidth;
      const s = Math.min(1, w / pageWidth);
      setScale(s);
      if (innerRef.current) setHeight(innerRef.current.offsetHeight * s);
    };
    measure();
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      if (boxRef.current) ro.observe(boxRef.current);
      if (innerRef.current) ro.observe(innerRef.current);
    } else {
      window.addEventListener("resize", measure);
    }
    return () => { if (ro) ro.disconnect(); else window.removeEventListener("resize", measure); };
  }, [pageWidth, data, phase]);
  return (
    <div ref={boxRef} style={{ width: "100%", minWidth: 0, display: "flex", justifyContent: "center", overflow: "hidden", padding: "4px 0 16px" }}>
      <div style={{ width: pageWidth * scale, height: height || undefined, overflow: "hidden", borderRadius: device === "phone" ? 18 : 10, boxShadow: "0 10px 30px rgba(0,0,0,.18)", background: "#F6F4EE", flexShrink: 0 }}>
        <div ref={innerRef} style={{ width: pageWidth, transform: `scale(${scale})`, transformOrigin: "top left" }}>
          <EventPageView data={data} narrow={device === "phone"} phase={phase} preview />
        </div>
      </div>
    </div>
  );
}

function Seg({ value, options, onChange }) {
  return (
    <div style={{ display: "inline-flex", background: "#e5e7eb", borderRadius: 8, padding: 2 }}>
      {options.map(([v, label]) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          style={{ border: "none", borderRadius: 6, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer", background: value === v ? "#fff" : "transparent", color: value === v ? "#111827" : "#4b5563", boxShadow: value === v ? "0 1px 2px rgba(0,0,0,.12)" : "none" }}>
          {label}
        </button>
      ))}
    </div>
  );
}

// --------------------------------------------------------------------------
// The organizer's own photo: upload (with the rights tick), focal point, replace, remove.
// --------------------------------------------------------------------------
const PHOTO_MAX_BYTES = 15 * 1024 * 1024;
const COMPUTER_RATIO = 1280 / 560;   // the hero band on a computer, wide
const PHONE_RATIO = 390 / 480;       // and on a phone, a tall slice

function OwnPhoto({ info, form, apiUrl, getToken, eventId, onInfo, onBackground, addToast }) {
  const up = info.upload;
  const selected = form.background.kind === "upload" && !!up;
  const [rights, setRights] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState([]);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const dragging = useRef(false);
  const abs = (u) => (u && u.startsWith("/public/") ? `${apiUrl}${u}` : u);

  const pick = async (file) => {
    setError("");
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type || "")) { setError("Use a JPG, PNG or WebP photo."); return; }
    if (file.size > PHOTO_MAX_BYTES) { setError("That photo is larger than 15 MB. Use a smaller file."); return; }
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("rights", "yes");
      fd.append("photo", file, file.name);
      const res = await fetch(`${apiUrl}/events/${eventId}/page/photo`, { method: "POST", credentials: "include", headers: authHeaders(getToken, false), body: fd });
      let data = null;
      try { data = await res.json(); } catch (_) { /* empty */ }
      if (!res.ok) throw new Error((data && data.error) || "The photo could not be uploaded.");
      setWarnings(data.photo_warnings || []);
      onInfo(data);
      onBackground({ kind: "upload", x: 50, y: 50 });
      addToast && addToast("Photo uploaded — drag the dot onto what matters most, then save", "success");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    setError("");
    setBusy("remove");
    try {
      const res = await fetch(`${apiUrl}/events/${eventId}/page/photo`, { method: "DELETE", credentials: "include", headers: authHeaders(getToken, false) });
      let data = null;
      try { data = await res.json(); } catch (_) { /* empty */ }
      if (!res.ok) throw new Error((data && data.error) || "The photo could not be removed.");
      setWarnings([]);
      onInfo(data);
      if (form.background.kind === "upload") onBackground({ kind: "library", key: "sunset-green" });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const moveTo = (e) => {
    const box = boxRef.current;
    if (!box) return;
    const r = box.getBoundingClientRect();
    const x = Math.round(Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100)));
    const y = Math.round(Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100)));
    onBackground({ kind: "upload", x, y });
  };

  const fileInput = (
    <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }}
      onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; pick(f); }} />
  );
  const rightsBox = (
    <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#374151", cursor: "pointer" }}>
      <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)} style={{ width: 15, height: 15, marginTop: 2 }} />
      <span>I took this photo or have permission to use it.</span>
    </label>
  );
  const requirements = (
    <div style={HINT}><strong>Photo requirements:</strong> JPG, PNG or WebP, up to 15 MB. At least 1,200 pixels wide (2,400+ looks sharpest), landscape. TTO darkens it slightly behind the title so the words always read, sizes it for phones, and removes the photo’s location and camera details.</div>
  );

  if (!up) {
    return (
      <div style={{ border: "1px dashed #9ca3af", borderRadius: 10, padding: 12, display: "grid", gap: 10, background: "#f9fafb" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Your own photo</div>
        {rightsBox}
        <div>
          {fileInput}
          <button type="button" disabled={!rights || !!busy} onClick={() => inputRef.current && inputRef.current.click()}
            style={{ background: rights ? GREEN : "#9ca3af", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, fontSize: 13, cursor: rights && !busy ? "pointer" : "default" }}>
            {busy === "upload" ? "Uploading…" : "Upload a photo"}
          </button>
        </div>
        {error && <div style={{ fontSize: 13, color: "#b91c1c" }}>{error}</div>}
        {requirements}
      </div>
    );
  }

  const W = up.width || 2400;
  const H = up.height || 1600;
  const bx = selected ? form.background.x : 50;
  const by = selected ? form.background.y : 50;
  // What each screen shows of the photo (CSS background-position semantics: x% of the
  // photo sits at x% of the screen).
  const compH = Math.min(1, (W / COMPUTER_RATIO) / H);
  const compTop = (1 - compH) * (by / 100);
  const phoneW = Math.min(1, (H * PHONE_RATIO) / W);
  const phoneLeft = (1 - phoneW) * (bx / 100);
  const soft = W < 2400 || warnings.includes("soft");
  const portrait = H > W || warnings.includes("portrait");

  return (
    <div style={{ border: selected ? `2px solid ${GREEN}` : "1px solid #d1d5db", borderRadius: 10, padding: 12, display: "grid", gap: 10, background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Your photo <span style={{ fontWeight: 500, color: "#6b7280" }}>· {W.toLocaleString("en-US")} × {H.toLocaleString("en-US")}</span></div>
        {!selected && (
          <button type="button" onClick={() => onBackground({ kind: "upload", x: 50, y: 50 })}
            style={{ background: GREEN, color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Use this photo</button>
        )}
      </div>
      <div ref={boxRef}
        onPointerDown={(e) => { if (!selected) return; dragging.current = true; try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* ok */ } moveTo(e); }}
        onPointerMove={(e) => { if (dragging.current) moveTo(e); }}
        onPointerUp={() => { dragging.current = false; }}
        onPointerCancel={() => { dragging.current = false; }}
        style={{ position: "relative", width: "100%", paddingTop: `${(H / W) * 100}%`, borderRadius: 8, overflow: "hidden", cursor: selected ? "crosshair" : "default", touchAction: "none", userSelect: "none",
          backgroundImage: `url(${abs(up.url_small)})`, backgroundSize: "100% 100%", opacity: selected ? 1 : 0.75 }}>
        {selected && (
          <>
            <div aria-hidden="true" title="What a computer shows" style={{ position: "absolute", left: 0, right: 0, top: `${compTop * 100}%`, height: `${compH * 100}%`, border: "2px solid rgba(255,255,255,.9)", boxShadow: "0 0 0 9999px rgba(0,0,0,.28)", boxSizing: "border-box", pointerEvents: "none" }} />
            <div aria-hidden="true" title="What a phone shows" style={{ position: "absolute", top: 0, bottom: 0, left: `${phoneLeft * 100}%`, width: `${phoneW * 100}%`, border: "2px dashed #E3BC5E", boxSizing: "border-box", pointerEvents: "none" }} />
            <div aria-label="Focus point" style={{ position: "absolute", left: `${bx}%`, top: `${by}%`, width: 22, height: 22, marginLeft: -11, marginTop: -11, borderRadius: "50%", background: "#E3BC5E", border: "3px solid #fff", boxShadow: "0 1px 6px rgba(0,0,0,.5)", pointerEvents: "none" }} />
          </>
        )}
      </div>
      {selected && (
        <div style={{ ...HINT, marginTop: 0 }}>
          Drag the dot onto what matters most. <span style={{ whiteSpace: "nowrap" }}>White frame = computer,</span> <span style={{ whiteSpace: "nowrap", color: "#92400e" }}>dashed gold = phone.</span>
        </div>
      )}
      {(soft || portrait) && (
        <div style={{ fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, padding: "6px 8px", lineHeight: 1.45 }}>
          {soft && <>This photo is {W.toLocaleString("en-US")} pixels wide. It works, but may look a little soft on large screens — 2,400 wide or more looks sharpest. </>}
          {portrait && <>It’s a tall (portrait) photo, so only a band across it will show on a computer — a wide photo works best.</>}
        </div>
      )}
      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        {fileInput}
        {rightsBox}
        <button type="button" disabled={!rights || !!busy} onClick={() => inputRef.current && inputRef.current.click()}
          style={{ background: "none", border: "none", color: rights ? GREEN : "#9ca3af", fontWeight: 700, fontSize: 13, cursor: rights && !busy ? "pointer" : "default", padding: 0 }}>
          {busy === "upload" ? "Uploading…" : "Replace photo"}
        </button>
        <button type="button" disabled={!!busy} onClick={remove}
          style={{ background: "none", border: "none", color: "#b91c1c", fontWeight: 600, fontSize: 13, cursor: busy ? "default" : "pointer", padding: 0 }}>
          {busy === "remove" ? "Removing…" : "Remove"}
        </button>
      </div>
      {error && <div style={{ fontSize: 13, color: "#b91c1c" }}>{error}</div>}
    </div>
  );
}

// --------------------------------------------------------------------------
// Sponsors (Oct 23, 2026): name, and optionally a logo and a website, in this order.
// --------------------------------------------------------------------------
const LOGO_MAX_BYTES = 6 * 1024 * 1024;

function SponsorRow({ sp, index, count, logoUrl, apiUrl, getToken, eventId, onChange, onMove, onRemove, onInfo }) {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const abs = (u) => (u && u.startsWith("/public/") ? `${apiUrl}${u}` : u);
  const call = async (method, file) => {
    setError("");
    setBusy(method === "POST" ? "upload" : "remove");
    try {
      let body;
      if (file) { body = new FormData(); body.append("logo", file, file.name); }
      const res = await fetch(`${apiUrl}/events/${eventId}/page/sponsors/${sp.id}/logo`, { method, credentials: "include", headers: authHeaders(getToken, false), body });
      let data = null;
      try { data = await res.json(); } catch (_) { /* empty */ }
      if (!res.ok) throw new Error((data && data.error) || (method === "POST" ? "The logo could not be uploaded." : "The logo could not be removed."));
      onInfo(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };
  const pick = (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type || "")) { setError("Use a PNG, JPG or WebP logo."); return; }
    if (file.size > LOGO_MAX_BYTES) { setError("That logo is larger than 6 MB. Use a smaller file."); return; }
    call("POST", file);
  };
  const small = { background: "none", border: "1px solid #d1d5db", borderRadius: 6, width: 30, height: 30, fontSize: 14, cursor: "pointer", color: "#374151", padding: 0 };
  return (
    <div data-testid="sponsor-row" style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, display: "grid", gap: 8, background: "#fff" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div style={{ width: 84, height: 52, borderRadius: 8, border: "1px dashed #cbd5e1", background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
          {logoUrl
            ? <img src={abs(logoUrl)} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            : <span style={{ fontSize: 11, color: "#9ca3af" }}>No logo</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: "grid", gap: 6 }}>
          <input style={FIELD} value={sp.name} maxLength={80} placeholder="Sponsor name" aria-label={`Sponsor ${index + 1} name`}
            onChange={(e) => onChange({ ...sp, name: e.target.value })} />
          <input style={FIELD} value={sp.url} maxLength={500} placeholder="Website (optional)" aria-label={`Sponsor ${index + 1} website`} spellCheck={false} autoCapitalize="none"
            onChange={(e) => onChange({ ...sp, url: e.target.value })} />
        </div>
        <div style={{ display: "grid", gap: 4, flexShrink: 0 }}>
          <button type="button" style={{ ...small, opacity: index === 0 ? 0.35 : 1 }} disabled={index === 0} onClick={() => onMove(-1)} aria-label="Move up">↑</button>
          <button type="button" style={{ ...small, opacity: index === count - 1 ? 0.35 : 1 }} disabled={index === count - 1} onClick={() => onMove(1)} aria-label="Move down">↓</button>
        </div>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; pick(f); }} />
        <button type="button" disabled={!!busy} onClick={() => inputRef.current && inputRef.current.click()}
          style={{ background: "none", border: "none", color: GREEN, fontWeight: 700, fontSize: 13, cursor: busy ? "default" : "pointer", padding: 0 }}>
          {busy === "upload" ? "Uploading…" : logoUrl ? "Replace logo" : "Upload logo"}
        </button>
        {logoUrl && (
          <button type="button" disabled={!!busy} onClick={() => call("DELETE")}
            style={{ background: "none", border: "none", color: "#6b7280", fontWeight: 600, fontSize: 13, cursor: busy ? "default" : "pointer", padding: 0 }}>
            {busy === "remove" ? "Removing…" : "Remove logo"}
          </button>
        )}
        <button type="button" onClick={onRemove} disabled={!!busy}
          style={{ marginLeft: "auto", background: "none", border: "none", color: "#b91c1c", fontWeight: 600, fontSize: 13, cursor: busy ? "default" : "pointer", padding: 0, opacity: busy ? 0.5 : 1 }}>
          Remove sponsor
        </button>
      </div>
      {error && <div style={{ fontSize: 13, color: "#b91c1c" }}>{error}</div>}
    </div>
  );
}

export function EventPageEditor({ eventId, initial, apiUrl, getToken, addToast, onChange, onClose }) {
  useEventPageFonts();
  const [info, setInfo] = useState(initial);
  const [form, setForm] = useState(() => formFrom(initial));
  const [savedForm, setSavedForm] = useState(() => JSON.stringify(formFrom(initial)));
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [confirmClose, setConfirmClose] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [phase, setPhase] = useState(() => phaseFor(initial.event_date));
  const [wide, setWide] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1000 : true));
  const [showPreview, setShowPreview] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const on = () => setWide(window.innerWidth >= 1000);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const dirty = JSON.stringify(form) !== savedForm;
  const set = (k) => (e) => { const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e; setForm((f) => ({ ...f, [k]: v })); setError(""); };
  const data = useMemo(() => previewData(form, info), [form, info]);
  const d = info.derived || {};
  const live = info.published;

  const save = async (publish) => {
    setError("");
    if (form.registration_mode === "link" && !form.registration_url.trim()) { setError("Add the web address the Register button should open, or choose No Register button."); return; }
    const logos = info.sponsor_logos || {};
    if (form.sponsors.some((x) => !x.name.trim() && (x.url.trim() || logos[x.id]))) { setError("Each sponsor needs a name — it shows under the logo."); return; }
    setBusy(publish === true ? "publish" : publish === false ? "unpublish" : "save");
    try {
      const { pay_fees, pay_methods, ...rest } = form;
      const body = { page: { ...rest, sponsors: form.sponsors.map((x) => ({ ...x, has_logo: !!logos[x.id] })), payment: { fees: pay_fees, methods: pay_methods } } };
      if (publish !== undefined) body.publish = publish;
      const out = await callPage(apiUrl, getToken, eventId, "PUT", body);
      setInfo(out);
      const nf = formFrom({ ...out, saved: true });
      setForm(nf);
      setSavedForm(JSON.stringify(nf));
      onChange && onChange(out);
      if (addToast) {
        if (publish === true) addToast(`Your event page is live at ${out.page_address}`, "success");
        else if (publish === false) addToast("Event page taken down", "success");
        else addToast(out.published ? "Saved — the live page shows your changes" : "Saved", "success");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const tryClose = () => { if (dirty) setConfirmClose(true); else onClose(); };
  const btn = (primary) => ({ border: primary ? "none" : "1px solid #d1d5db", background: primary ? GREEN : "#fff", color: primary ? "#fff" : "#111827", borderRadius: 8, padding: "9px 14px", fontWeight: 700, fontSize: 14, cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1, whiteSpace: "nowrap" });

  const lib = info.library || [];
  const colors = info.colors || [];
  const bgSel = form.background;
  const setSponsors = (fn) => { setForm((f) => ({ ...f, sponsors: fn(f.sponsors) })); setError(""); };
  const setFees = (fn) => { setForm((f) => ({ ...f, pay_fees: fn(f.pay_fees) })); setError(""); };
  const setMethod = (type, patch) => { setForm((f) => ({ ...f, pay_methods: { ...f.pay_methods, [type]: { ...f.pay_methods[type], ...patch } } })); setError(""); };
  const anyPay = PAY_METHODS.some((m) => form.pay_methods[m.type] && form.pay_methods[m.type].on);

  const formCol = (
    <div style={{ display: "grid", gap: 14 }}>
      <section style={SECTION}>
        <h2 style={H2}>Background</h2>
        <OwnPhoto info={info} form={form} apiUrl={apiUrl} getToken={getToken} eventId={eventId}
          onInfo={(out) => { setInfo(out); onChange && onChange(out); }}
          onBackground={(bg) => set("background")(bg)} addToast={addToast} />
        <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", margin: "14px 0 8px" }}>Or choose one of ours</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(118px, 1fr))", gap: 10 }}>
          {lib.map((p) => {
            const on = bgSel.kind === "library" && bgSel.key === p.key;
            return (
              <button key={p.key} type="button" onClick={() => set("background")({ kind: "library", key: p.key })} aria-pressed={on}
                style={{ padding: 0, border: on ? `3px solid ${GREEN}` : "1px solid #d1d5db", borderRadius: 10, overflow: "hidden", cursor: "pointer", background: "#fff", textAlign: "left" }}>
                <div aria-hidden="true" style={{ height: 66, backgroundImage: `url(/event-backgrounds/${p.key}-thumb.webp)`, backgroundSize: "cover", backgroundPosition: p.position }} />
                <div style={{ fontSize: 12, fontWeight: 600, padding: "5px 8px", color: "#111827" }}>{p.label}</div>
              </button>
            );
          })}
          {colors.map((c) => {
            const on = bgSel.kind === "color" && bgSel.key === c.key;
            return (
              <button key={c.key} type="button" onClick={() => set("background")({ kind: "color", key: c.key })} aria-pressed={on}
                style={{ padding: 0, border: on ? `3px solid ${GREEN}` : "1px solid #d1d5db", borderRadius: 10, overflow: "hidden", cursor: "pointer", background: "#fff", textAlign: "left" }}>
                <div aria-hidden="true" style={{ height: 66, background: c.bg }} />
                <div style={{ fontSize: 12, fontWeight: 600, padding: "5px 8px", color: "#111827" }}>{c.label}</div>
              </button>
            );
          })}
        </div>
        <div style={HINT}>{info.has_logo ? "Your event logo (from Set up event) shows at the top." : "Add an event logo in Set up event and it shows at the top."}</div>
      </section>

      <section style={SECTION}>
        <h2 style={H2}>Headline</h2>
        <div style={{ display: "grid", gap: 12 }}>
          <Field label="Presented by (optional)" hint="Shown above the title, e.g. “Seacoast Church presents”.">
            <input style={FIELD} value={form.presented_by} onChange={set("presented_by")} maxLength={80} placeholder="e.g. Seacoast Church" />
          </Field>
          <Field label="Event name" hint="From your event — change it here for the page only.">
            <input style={FIELD} value={form.title} onChange={set("title")} maxLength={120} placeholder={d.title} />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Date"><input style={FIELD} value={form.date_text} onChange={set("date_text")} maxLength={80} placeholder={d.date_text} /></Field>
            <Field label="Course"><input style={FIELD} value={form.course_text} onChange={set("course_text")} maxLength={120} placeholder={d.course_text} /></Field>
            <Field label="Check-in (optional)"><input style={FIELD} value={form.checkin_text} onChange={set("checkin_text")} maxLength={60} placeholder="e.g. 11:00 AM" /></Field>
            <Field label="Start"><input style={FIELD} value={form.start_text} onChange={set("start_text")} maxLength={80} placeholder={d.start_text} /></Field>
          </div>
          <Field label="Format (optional)"><input style={FIELD} value={form.format_text} onChange={set("format_text")} maxLength={120} placeholder="e.g. Four-person captain’s choice" /></Field>
          <div style={{ ...HINT, marginTop: 0 }}>Date, course and start come from your event and follow it if the tee time changes — unless you type something different here.</div>
        </div>
      </section>

      <section style={SECTION}>
        <h2 style={H2}>About the event</h2>
        <textarea style={{ ...FIELD, minHeight: 170, resize: "vertical", lineHeight: 1.5 }} value={form.description} onChange={set("description")} maxLength={5000}
          placeholder="What it is, who it supports, what the entry includes, prizes…" />
        <div style={HINT}>Plain text. A blank line starts a new paragraph; web addresses and emails become links.</div>
      </section>

      <section style={SECTION}>
        <h2 style={H2}>Registration</h2>
        <div style={{ display: "grid", gap: 8 }}>
          {[["none", "No Register button"], ["link", "My own registration page"], ["invite", "This event’s TTO sign-up link"]].map(([v, label]) => (
            <label key={v} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
              <input type="radio" name="evp-reg" checked={form.registration_mode === v} onChange={() => set("registration_mode")(v)} style={{ width: 16, height: 16 }} /> {label}
            </label>
          ))}
        </div>
        {form.registration_mode === "link" && (
          <div style={{ marginTop: 10 }}>
            <Field label="Where it goes" hint="Your church, club or charity page — e.g. seacoast.org/turkeyshoot.">
              <input style={FIELD} value={form.registration_url} onChange={set("registration_url")} maxLength={500} placeholder="https://" spellCheck={false} autoCapitalize="none" />
            </Field>
          </div>
        )}
        {form.registration_mode === "invite" && (
          <div style={HINT}>Golfers sign themselves up for this event in TTO{info.invite_url ? <> — <a href={info.invite_url} target="_blank" rel="noreferrer" style={{ color: GREEN }}>{info.invite_url.replace(/^https?:\/\//, "")}</a></> : " (the link is made when you save)"}.</div>
        )}
        {form.registration_mode !== "none" && (
          <div style={{ marginTop: 10 }}>
            <Field label="Button wording">
              <input style={FIELD} value={form.registration_label} onChange={set("registration_label")} maxLength={40} placeholder={form.registration_mode === "invite" ? "Sign up" : "Register"} />
            </Field>
          </div>
        )}
      </section>

      <section style={SECTION} data-testid="evp-pay-setup">
        <h2 style={H2}>Entry fees &amp; how to pay <span style={{ fontWeight: 500, color: "#6b7280", fontSize: 13 }}>(optional)</span></h2>
        <div style={{ ...HINT, marginTop: 0, marginBottom: 12, fontSize: 13, color: "#374151" }}>
          Tick the ways you already collect money. Golfers get one-tap buttons with the right amount and a note saying what it’s for. <strong>The money goes straight to you — Tee Time Organizer never touches it.</strong> Tick nothing and the page and emails say nothing about paying.
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 6 }}>Prices</div>
        <div style={{ display: "grid", gap: 8 }}>
          {form.pay_fees.map((f, i) => (
            <div key={i} data-testid="pay-fee-row" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto 104px auto", gap: 6, alignItems: "center" }}>
              <input style={FIELD} value={f.label} maxLength={40} placeholder={i === 0 ? "e.g. Single player" : "e.g. Team of 4"} aria-label={`Price ${i + 1} name`}
                onChange={(e) => setFees((list) => list.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <select style={{ ...FIELD, width: "auto", padding: "9px 6px" }} value={f.per} aria-label={`Price ${i + 1} is for`}
                onChange={(e) => setFees((list) => list.map((x, j) => (j === i ? { ...x, per: e.target.value } : x)))}>
                <option value="golfer">Per golfer</option>
                <option value="team">Per team</option>
              </select>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 10, top: 9, fontSize: 14, color: "#6b7280" }}>$</span>
                <input style={{ ...FIELD, paddingLeft: 20 }} value={f.amount} inputMode="decimal" maxLength={10} placeholder="150" aria-label={`Price ${i + 1} amount`}
                  onChange={(e) => setFees((list) => list.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} />
              </div>
              <button type="button" aria-label={`Remove price ${i + 1}`} onClick={() => setFees((list) => list.filter((_, j) => j !== i))}
                style={{ background: "none", border: "none", color: "#b91c1c", fontSize: 20, cursor: "pointer", padding: "0 4px" }}>×</button>
            </div>
          ))}
          <div>
            <button type="button" data-testid="pay-fee-add" disabled={form.pay_fees.length >= 4}
              onClick={() => setFees((list) => [...list, { label: "", per: list.length ? "team" : "golfer", amount: "" }])}
              style={{ background: "#fff", border: `1px solid ${GREEN}`, color: GREEN, borderRadius: 8, padding: "7px 12px", fontWeight: 700, fontSize: 13, cursor: form.pay_fees.length >= 4 ? "default" : "pointer", opacity: form.pay_fees.length >= 4 ? 0.5 : 1 }}>
              + Add a price
            </button>
          </div>
        </div>
        <div style={HINT}>A golfer who signs up alone through Tee Time Organizer is asked for the first <em>Per golfer</em> price. With two or more prices, visitors to the page choose which they’re paying.</div>

        <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", margin: "16px 0 6px" }}>Ways to pay</div>
        <div style={{ display: "grid", gap: 8 }}>
          {PAY_METHODS.map((m) => {
            const f = form.pay_methods[m.type];
            return (
              <div key={m.type} style={{ border: f.on ? `1.5px solid ${GREEN}` : "1px solid #e5e7eb", borderRadius: 10, padding: "8px 10px", background: f.on ? "#f0fdf4" : "#fff" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", minHeight: 28 }}>
                  <input type="checkbox" checked={f.on} data-testid={`pay-on-${m.type}`} onChange={(e) => setMethod(m.type, { on: e.target.checked })} style={{ width: 17, height: 17 }} />
                  {m.name}
                </label>
                {f.on && m.type === "link" && (
                  <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                    <input style={FIELD} value={f.url} maxLength={500} placeholder={m.placeholder} aria-label="Pay-online web address" spellCheck={false} autoCapitalize="none" onChange={(e) => setMethod(m.type, { url: e.target.value })} />
                    <input style={FIELD} value={f.label} maxLength={40} placeholder="Button wording, e.g. Pay on our website" aria-label="Pay-online button wording" onChange={(e) => setMethod(m.type, { label: e.target.value })} />
                  </div>
                )}
                {f.on && m.type === "checkin" && (
                  <input style={{ ...FIELD, marginTop: 8 }} value={f.note} maxLength={80} placeholder="Optional note, e.g. Cash, check or card" aria-label="Check-in payment note" onChange={(e) => setMethod(m.type, { note: e.target.value })} />
                )}
                {f.on && m.field && m.type !== "link" && (
                  <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                    <input style={FIELD} value={f.handle} maxLength={160} placeholder={m.placeholder} aria-label={m.field} spellCheck={false} autoCapitalize="none" data-testid={`pay-handle-${m.type}`} onChange={(e) => setMethod(m.type, { handle: e.target.value })} />
                    {m.type === "check" && (
                      <textarea style={{ ...FIELD, minHeight: 60, resize: "vertical" }} value={f.note} maxLength={200} placeholder="Mailing address (optional)" aria-label="Mailing address for checks" onChange={(e) => setMethod(m.type, { note: e.target.value })} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {anyPay && (
          <div style={HINT}>Once it’s published, tap each button yourself on your phone to check it opens your account. Zelle has no link — golfers are shown your email or number to send to.</div>
        )}
      </section>

      <section style={SECTION}>
        <h2 style={H2}>Donate button <span style={{ fontWeight: 500, color: "#6b7280", fontSize: 13 }}>(optional)</span></h2>
        <div style={{ display: "grid", gap: 12 }}>
          <Field label="Where it goes" hint="Your church, club or charity’s own giving, auction or raffle page. It opens there in a new tab — TTO never handles the money.">
            <input style={FIELD} value={form.donate_url} onChange={set("donate_url")} maxLength={500} placeholder="e.g. seacoast.org/give" spellCheck={false} autoCapitalize="none" data-testid="evp-donate-url" />
          </Field>
          <Field label="Button wording">
            <input style={FIELD} value={form.donate_label} onChange={set("donate_label")} maxLength={40} placeholder="Donate" />
          </Field>
        </div>
        <div style={HINT}>Leave the address empty and there’s no button. It shows on this page only.</div>
      </section>

      <section style={SECTION}>
        <h2 style={H2}>Questions? <span style={{ fontWeight: 500, color: "#6b7280", fontSize: 13 }}>(each optional)</span></h2>
        <div style={{ display: "grid", gap: 12 }}>
          <Field label="Contact name"><input style={FIELD} value={form.contact_name} onChange={set("contact_name")} maxLength={80} placeholder="e.g. Blake" /></Field>
          <Field label="Email"><input type="email" style={FIELD} value={form.contact_email} onChange={set("contact_email")} maxLength={120} placeholder="name@example.org" /></Field>
          <Field label="Phone"><input type="tel" style={FIELD} value={form.contact_phone} onChange={set("contact_phone")} maxLength={40} placeholder="(843) 555-0100" /></Field>
        </div>
      </section>

      <section style={SECTION}>
        <h2 style={H2}>Live leaderboard</h2>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={form.show_leaderboard} onChange={set("show_leaderboard")} style={{ width: 16, height: 16, marginTop: 2 }} />
          <span>Tell visitors they can follow the scores at <strong>{info.leaderboard_address}</strong></span>
        </label>
        <div style={HINT}>Before the event the page says when scoring starts. On the day the main button becomes Live leaderboard, and afterwards See the results.</div>
      </section>

      <section style={SECTION}>
        <h2 style={H2}>Sponsors <span style={{ fontWeight: 500, color: "#6b7280", fontSize: 13 }}>(optional)</span></h2>
        <div style={{ display: "grid", gap: 10 }}>
          {form.sponsors.map((sp, i) => (
            <SponsorRow key={sp.id} sp={sp} index={i} count={form.sponsors.length} logoUrl={(info.sponsor_logos || {})[sp.id] || null}
              apiUrl={apiUrl} getToken={getToken} eventId={eventId}
              onInfo={(out) => { setInfo(out); onChange && onChange(out); }}
              onChange={(next) => setSponsors((list) => list.map((x) => (x.id === sp.id ? next : x)))}
              onMove={(dir) => setSponsors((list) => { const a = list.slice(); const j = i + dir; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; })}
              onRemove={() => setSponsors((list) => list.filter((x) => x.id !== sp.id))} />
          ))}
          <div>
            <button type="button" data-testid="sponsor-add" disabled={form.sponsors.length >= 40}
              onClick={() => setSponsors((list) => [...list, { id: newSponsorId(), name: "", url: "" }])}
              style={{ background: "#fff", border: `1px solid ${GREEN}`, color: GREEN, borderRadius: 8, padding: "8px 14px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              + Add a sponsor
            </button>
          </div>
        </div>
        <div style={HINT}>Shown as a grid of logos at the bottom of the page, in this order, each with the name under it. A sponsor with a website opens it in a new tab. No logo? The tile shows their initials. A logo uploads straight away — on a live page it shows at once; names, links and order wait for Save. PNG with a clear background looks best.</div>
      </section>
    </div>
  );

  const previewCol = (
    <div style={{ display: "grid", gap: 12, alignContent: "start" }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
        <Seg value={device} onChange={setDevice} options={[["desktop", "Computer"], ["phone", "Phone"]]} />
        <Seg value={phase} onChange={setPhase} options={[["before", "Before"], ["today", "On the day"], ["after", "After"]]} />
      </div>
      <ScaledPreview data={data} device={device} phase={phase} />
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 95, background: "#f3f4f6", display: "flex", flexDirection: "column" }} role="dialog" aria-modal="true" aria-label="Event page">
      <div style={{ background: "linear-gradient(135deg, #2d6a4f, #1b4332)", color: "#fff", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 240px", minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Event page</div>
          <div style={{ fontSize: 13, opacity: 0.92, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {live ? <>● Live at <a href={info.page_url} target="_blank" rel="noreferrer" style={{ color: "#fff", fontWeight: 700 }}>{info.page_address}</a></> : <>Not published · will be at <strong>{info.page_address}</strong></>}
            {live && (
              <button type="button" onClick={() => copyText(info.page_url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {})}
                style={{ marginLeft: 10, background: "rgba(255,255,255,.18)", border: "none", color: "#fff", borderRadius: 6, padding: "2px 8px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {copied ? "✓ Copied" : "Copy link"}
              </button>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" style={btn(false)} onClick={tryClose} disabled={!!busy}>Close</button>
          {live && <button type="button" style={btn(false)} onClick={() => save(false)} disabled={!!busy}>{busy === "unpublish" ? "Taking down…" : "Unpublish"}</button>}
          {live
            ? <button type="button" style={{ ...btn(true), background: "#D9B45A", color: "#1D2420" }} onClick={() => save()} disabled={!!busy || !dirty}>{busy === "save" ? "Saving…" : dirty ? "Save changes" : "Saved"}</button>
            : <>
                <button type="button" style={btn(false)} onClick={() => save()} disabled={!!busy || !dirty}>{busy === "save" ? "Saving…" : "Save draft"}</button>
                <button type="button" style={{ ...btn(true), background: "#D9B45A", color: "#1D2420" }} onClick={() => save(true)} disabled={!!busy}>{busy === "publish" ? "Publishing…" : "Publish"}</button>
              </>}
        </div>
      </div>
      {(error || confirmClose) && (
        <div style={{ padding: "10px 16px", background: error ? "#fef2f2" : "#fffbeb", borderBottom: `1px solid ${error ? "#fecaca" : "#fcd34d"}`, fontSize: 14, color: error ? "#991b1b" : "#92400e", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          {error ? <span>{error}</span> : (
            <>
              <span>You have changes that aren’t saved.</span>
              <button type="button" style={btn(false)} onClick={() => setConfirmClose(false)}>Keep editing</button>
              <button type="button" style={{ ...btn(false), color: "#b91c1c" }} onClick={onClose}>Discard changes</button>
            </>
          )}
        </div>
      )}
      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {wide ? (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(380px, 460px) minmax(0, 1fr)", gap: 20, maxWidth: 1500, margin: "0 auto", alignItems: "start" }}>
            {formCol}
            <div style={{ position: "sticky", top: 0, maxHeight: "calc(100vh - 120px)", overflowY: "auto", overflowX: "hidden", paddingBottom: 12, minWidth: 0 }}>{previewCol}</div>
          </div>
        ) : (
          <div style={{ maxWidth: 640, margin: "0 auto", display: "grid", gap: 14 }}>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Seg value={showPreview ? "preview" : "edit"} onChange={(v) => setShowPreview(v === "preview")} options={[["edit", "Edit"], ["preview", "Preview"]]} />
            </div>
            {showPreview ? previewCol : formCol}
          </div>
        )}
      </div>
    </div>
  );
}
